import React, { useState, useRef, useEffect, useCallback } from "react";
import Kick from "../components/Kick";
import Snare from "../components/Snare";
import HiHat from "../components/HiHat";
import Sequencer from "../components/Sequencer";
import AutomationLane from "../components/AutomationLane";
import { Start } from "../components/Start";
import * as Tone from "tone";
import { Box, Grommet } from "grommet";
import { SelectBPM } from "../components/SelectBPM";
import { custom } from "../theme";

const TIMING_SHIFT_STEP_MS = 5;
const TIMING_SHIFT_STEP_PX = 5;
const MAX_TIMING_SHIFT_STEPS = 6;
const AUTOMATION_STEPS = 16;

const clampTimingShiftStep = (step) =>
  Math.min(Math.max(step, -MAX_TIMING_SHIFT_STEPS), MAX_TIMING_SHIFT_STEPS);

const createAutomationValues = (value) =>
  Array.from({ length: AUTOMATION_STEPS }, () => value);

const getAutomationValue = (values, stepIndex) =>
  values[stepIndex % values.length] ?? values[0];

const SynthContainer = () => {
  const [playing, setPlaying] = useState(false);
  const [BPM, setBPM] = useState(120);
  const [kickPattern, setKickPatternState] = useState([
    1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0,
  ]);
  const [snarePattern, setSnarePatternState] = useState([
    0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0,
  ]);
  const [hiHatPattern, setHiHatPatternState] = useState([
    1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0,
  ]);
  const [activeStep, setActiveStep] = useState(0);
  const [kickTimingShiftSteps, setKickTimingShiftSteps] = useState(0);
  const [snareTimingShiftSteps, setSnareTimingShiftSteps] = useState(0);
  const [hiHatTimingShiftSteps, setHiHatTimingShiftSteps] = useState(0);
  const [kickAutomationOpen, setKickAutomationOpen] = useState(false);
  const [snareAutomationOpen, setSnareAutomationOpen] = useState(false);
  const [hiHatAutomationOpen, setHiHatAutomationOpen] = useState(false);
  const [kickPitchAutomationEnabled, setKickPitchAutomationEnabled] =
    useState(false);
  const [snareDecayAutomationEnabled, setSnareDecayAutomationEnabled] =
    useState(false);
  const [hiHatDecayAutomationEnabled, setHiHatDecayAutomationEnabled] =
    useState(false);
  const [kickPitchAutomation, setKickPitchAutomation] = useState(() =>
    createAutomationValues(4),
  );
  const [snareDecayAutomation, setSnareDecayAutomation] = useState(() =>
    createAutomationValues(0.4),
  );
  const [hiHatDecayAutomation, setHiHatDecayAutomation] = useState(() =>
    createAutomationValues(0.07),
  );

  const kickRef = useRef(null);
  const snareRef = useRef(null);
  const hiHatRef = useRef(null);
  const kickPitchAutomationRef = useRef(kickPitchAutomation);
  const snareDecayAutomationRef = useRef(snareDecayAutomation);
  const hiHatDecayAutomationRef = useRef(hiHatDecayAutomation);
  const kickPitchAutomationEnabledRef = useRef(kickPitchAutomationEnabled);
  const snareDecayAutomationEnabledRef = useRef(snareDecayAutomationEnabled);
  const hiHatDecayAutomationEnabledRef = useRef(hiHatDecayAutomationEnabled);
  const kickTimingOffsetRef = useRef(0);
  const snareTimingOffsetRef = useRef(0);
  const hiHatTimingOffsetRef = useRef(0);
  const loopRef = useRef(null);
  const stepIndexRef = useRef(0);
  const kickPatternRef = useRef(kickPattern);
  const snarePatternRef = useRef(snarePattern);
  const hiHatPatternRef = useRef(hiHatPattern);
  const patternLengthRef = useRef(
    Math.max(kickPattern.length, snarePattern.length, hiHatPattern.length),
  );
  const playingRef = useRef(playing);
  const resumePromiseRef = useRef(null);

  useEffect(() => {
    if (Tone.Transport.bpm) {
      Tone.Transport.bpm.value = BPM;
    }
  }, [BPM]);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    kickPitchAutomationRef.current = kickPitchAutomation;
  }, [kickPitchAutomation]);

  useEffect(() => {
    snareDecayAutomationRef.current = snareDecayAutomation;
  }, [snareDecayAutomation]);

  useEffect(() => {
    hiHatDecayAutomationRef.current = hiHatDecayAutomation;
  }, [hiHatDecayAutomation]);

  useEffect(() => {
    kickPitchAutomationEnabledRef.current = kickPitchAutomationEnabled;
  }, [kickPitchAutomationEnabled]);

  useEffect(() => {
    snareDecayAutomationEnabledRef.current = snareDecayAutomationEnabled;
  }, [snareDecayAutomationEnabled]);

  useEffect(() => {
    hiHatDecayAutomationEnabledRef.current = hiHatDecayAutomationEnabled;
  }, [hiHatDecayAutomationEnabled]);

  const stopSequencer = useCallback(() => {
    Tone.Transport.stop?.();
    playingRef.current = false;
    setPlaying(false);
    stepIndexRef.current = 0;
    setActiveStep(0);
  }, []);

  const resumeAudio = useCallback(async () => {
    if (Tone.context.state === "running") {
      return true;
    }

    if (!resumePromiseRef.current) {
      resumePromiseRef.current = Tone.start()
        .then(() => Tone.context.state === "running")
        .catch((error) => {
          console.warn("AudioContext start was blocked:", error);
          return false;
        })
        .finally(() => {
          resumePromiseRef.current = null;
        });
    }

    return resumePromiseRef.current;
  }, []);

  useEffect(() => {
    const handleStateChange = (state) => {
      console.info(`[AudioContext] state=${state}`);
      if (state !== "running" && playingRef.current) {
        stopSequencer();
      }
    };

    console.info(`[AudioContext] initial=${Tone.context.state}`);
    Tone.context.on?.("statechange", handleStateChange);

    const handleTransportStart = (time) => {
      console.info(`[Transport] start time=${time}`);
    };
    const handleTransportStop = (time) => {
      console.info(`[Transport] stop time=${time}`);
    };
    const handleTransportPause = (time) => {
      console.info(`[Transport] pause time=${time}`);
    };

    Tone.Transport.on?.("start", handleTransportStart);
    Tone.Transport.on?.("stop", handleTransportStop);
    Tone.Transport.on?.("pause", handleTransportPause);

    return () => {
      Tone.context.off?.("statechange", handleStateChange);
      Tone.Transport.off?.("start", handleTransportStart);
      Tone.Transport.off?.("stop", handleTransportStop);
      Tone.Transport.off?.("pause", handleTransportPause);
    };
  }, [stopSequencer]);

  useEffect(() => {
    const resumeOnGesture = () => {
      resumeAudio();
    };

    window.addEventListener("pointerdown", resumeOnGesture);
    window.addEventListener("keydown", resumeOnGesture);

    return () => {
      window.removeEventListener("pointerdown", resumeOnGesture);
      window.removeEventListener("keydown", resumeOnGesture);
    };
  }, [resumeAudio]);

  const updatePatternLength = useCallback((kick, snare, hiHat) => {
    const length = Math.max(kick.length, snare.length, hiHat.length);
    patternLengthRef.current = length > 0 ? length : 1;
    if (stepIndexRef.current >= patternLengthRef.current) {
      stepIndexRef.current = 0;
    }
  }, []);

  const setKickPattern = useCallback(
    (newPattern) => {
      kickPatternRef.current = newPattern;
      setKickPatternState(newPattern);
      updatePatternLength(
        newPattern,
        snarePatternRef.current,
        hiHatPatternRef.current,
      );
    },
    [updatePatternLength],
  );

  const setSnarePattern = useCallback(
    (newPattern) => {
      snarePatternRef.current = newPattern;
      setSnarePatternState(newPattern);
      updatePatternLength(
        kickPatternRef.current,
        newPattern,
        hiHatPatternRef.current,
      );
    },
    [updatePatternLength],
  );

  const setHiHatPattern = useCallback(
    (newPattern) => {
      hiHatPatternRef.current = newPattern;
      setHiHatPatternState(newPattern);
      updatePatternLength(
        kickPatternRef.current,
        snarePatternRef.current,
        newPattern,
      );
    },
    [updatePatternLength],
  );

  const shiftTimingOffset = useCallback((setShiftSteps, offsetRef, amount) => {
    setShiftSteps((currentSteps) => {
      const nextSteps = clampTimingShiftStep(currentSteps + amount);
      offsetRef.current = nextSteps * TIMING_SHIFT_STEP_MS;
      return nextSteps;
    });
  }, []);

  const shiftKickEarlier = useCallback(() => {
    shiftTimingOffset(setKickTimingShiftSteps, kickTimingOffsetRef, -1);
  }, [shiftTimingOffset]);

  const shiftKickLater = useCallback(() => {
    shiftTimingOffset(setKickTimingShiftSteps, kickTimingOffsetRef, 1);
  }, [shiftTimingOffset]);

  const shiftSnareEarlier = useCallback(() => {
    shiftTimingOffset(setSnareTimingShiftSteps, snareTimingOffsetRef, -1);
  }, [shiftTimingOffset]);

  const shiftSnareLater = useCallback(() => {
    shiftTimingOffset(setSnareTimingShiftSteps, snareTimingOffsetRef, 1);
  }, [shiftTimingOffset]);

  const shiftHiHatEarlier = useCallback(() => {
    shiftTimingOffset(setHiHatTimingShiftSteps, hiHatTimingOffsetRef, -1);
  }, [shiftTimingOffset]);

  const shiftHiHatLater = useCallback(() => {
    shiftTimingOffset(setHiHatTimingShiftSteps, hiHatTimingOffsetRef, 1);
  }, [shiftTimingOffset]);

  useEffect(() => {
    if (typeof AudioBuffer === "undefined") {
      return undefined;
    }

    loopRef.current = new Tone.Loop((time) => {
      const length = patternLengthRef.current;
      if (!length || Tone.context.state !== "running") {
        return;
      }

      const stepIndex = stepIndexRef.current % length;
      const kickStep = kickPatternRef.current[stepIndex];
      const snareStep = snarePatternRef.current[stepIndex];
      const hiHatStep = hiHatPatternRef.current[stepIndex];

      if (kickStep === 1 && kickRef.current) {
        const automation = kickPitchAutomationEnabledRef.current
          ? {
              pitch: getAutomationValue(
                kickPitchAutomationRef.current,
                stepIndex,
              ),
            }
          : undefined;

        kickRef.current.playSynth(
          time + kickTimingOffsetRef.current / 1000,
          automation,
        );
      }

      if (snareStep === 1 && snareRef.current) {
        const automation = snareDecayAutomationEnabledRef.current
          ? {
              decay: getAutomationValue(
                snareDecayAutomationRef.current,
                stepIndex,
              ),
            }
          : undefined;

        snareRef.current.playSynth(
          time + snareTimingOffsetRef.current / 1000,
          automation,
        );
      }

      if (hiHatStep === 1 && hiHatRef.current) {
        const automation = hiHatDecayAutomationEnabledRef.current
          ? {
              decay: getAutomationValue(
                hiHatDecayAutomationRef.current,
                stepIndex,
              ),
            }
          : undefined;

        hiHatRef.current.playSynth(
          time + hiHatTimingOffsetRef.current / 1000,
          automation,
        );
      }

      Tone.Draw.schedule(() => {
        setActiveStep(stepIndex);
      }, time);

      stepIndexRef.current = (stepIndex + 1) % length;
    }, "16n").start(0);

    return () => {
      loopRef.current?.dispose();
      loopRef.current = null;
      Tone.Transport.stop?.();
    };
  }, []);

  const handleStartStop = useCallback(async () => {
    if (!playing) {
      if (
        !kickRef.current?.prepareSynth() ||
        !snareRef.current?.prepareSynth() ||
        !hiHatRef.current?.prepareSynth()
      ) {
        return;
      }

      const canPlay = await resumeAudio();
      if (!canPlay) {
        return;
      }

      Tone.Transport.start("+0.05");
      playingRef.current = true;
      setPlaying(true);
      return;
    }

    stopSequencer();
  }, [playing, resumeAudio, stopSequencer]);
  return (
    <>
      <Grommet theme={custom}>
        <Box
          className="transport-bar"
          flex={false}
          direction="row"
          pad="small"
          align="center"
          justify="start"
          height="min-content"
          responsive={false}
        >
          <SelectBPM bpm={BPM} setBPM={setBPM} />
          <Start playing={playing} onToggle={handleStartStop} />
        </Box>

        <div className="sequencer-stack">
          <div className="sequencer-track">
            <Sequencer
              sequence={kickPattern}
              setSequence={setKickPattern}
              activeStep={activeStep}
              instrumentName="kick"
              maxShiftSteps={MAX_TIMING_SHIFT_STEPS}
              onShiftLeft={shiftKickEarlier}
              onShiftRight={shiftKickLater}
              shiftStepMs={TIMING_SHIFT_STEP_MS}
              shiftStepPx={TIMING_SHIFT_STEP_PX}
              shiftSteps={kickTimingShiftSteps}
            />
            <AutomationLane
              enabled={kickPitchAutomationEnabled}
              isOpen={kickAutomationOpen}
              label="Pitch"
              maxValue={25}
              minValue={0}
              onEnabledChange={setKickPitchAutomationEnabled}
              onOpenChange={setKickAutomationOpen}
              setValues={setKickPitchAutomation}
              shiftStepPx={TIMING_SHIFT_STEP_PX}
              shiftSteps={kickTimingShiftSteps}
              stepValue={1}
              values={kickPitchAutomation}
            />
          </div>
          <div className="sequencer-track">
            <Sequencer
              sequence={snarePattern}
              setSequence={setSnarePattern}
              activeStep={activeStep}
              instrumentName="snare"
              maxShiftSteps={MAX_TIMING_SHIFT_STEPS}
              onShiftLeft={shiftSnareEarlier}
              onShiftRight={shiftSnareLater}
              shiftStepMs={TIMING_SHIFT_STEP_MS}
              shiftStepPx={TIMING_SHIFT_STEP_PX}
              shiftSteps={snareTimingShiftSteps}
            />
            <AutomationLane
              enabled={snareDecayAutomationEnabled}
              isOpen={snareAutomationOpen}
              label="Decay"
              maxValue={0.5}
              minValue={0.01}
              onEnabledChange={setSnareDecayAutomationEnabled}
              onOpenChange={setSnareAutomationOpen}
              setValues={setSnareDecayAutomation}
              shiftStepPx={TIMING_SHIFT_STEP_PX}
              shiftSteps={snareTimingShiftSteps}
              stepValue={0.01}
              values={snareDecayAutomation}
            />
          </div>
          <div className="sequencer-track">
            <Sequencer
              sequence={hiHatPattern}
              setSequence={setHiHatPattern}
              activeStep={activeStep}
              instrumentName="hi-hat"
              maxShiftSteps={MAX_TIMING_SHIFT_STEPS}
              onShiftLeft={shiftHiHatEarlier}
              onShiftRight={shiftHiHatLater}
              shiftStepMs={TIMING_SHIFT_STEP_MS}
              shiftStepPx={TIMING_SHIFT_STEP_PX}
              shiftSteps={hiHatTimingShiftSteps}
            />
            <AutomationLane
              enabled={hiHatDecayAutomationEnabled}
              isOpen={hiHatAutomationOpen}
              label="Decay"
              maxValue={0.3}
              minValue={0.01}
              onEnabledChange={setHiHatDecayAutomationEnabled}
              onOpenChange={setHiHatAutomationOpen}
              setValues={setHiHatDecayAutomation}
              shiftStepPx={TIMING_SHIFT_STEP_PX}
              shiftSteps={hiHatTimingShiftSteps}
              stepValue={0.01}
              values={hiHatDecayAutomation}
            />
          </div>
        </div>

        <Box className="instrument-rack" direction="row" responsive={false}>
          <Kick ref={kickRef} />
          <Snare ref={snareRef} />
          <HiHat ref={hiHatRef} />
        </Box>
      </Grommet>
    </>
  );
};

export default SynthContainer;
